import React, { useState, useMemo } from 'react';
import type { Notify } from '../../hooks';
import { useChurchConfig, saveOptionList } from '../../hooks';
import { Input, Button } from '../../components/ui';
import type { Tokens } from '../../theme/tokens';
import { MODULES, MODULE_BY_ID, SETTINGS_TILE } from '../../utils/constants';
import { OPTION_LISTS, parseOptionLists } from '../../utils/optionLists';
import type { OptionListDefinition } from '../../utils/optionLists';

// Every dropdown list in one place: the event types, designations, expense
// heads and so on that users pick from (and add to) inside each module's form.
// Each card saves on its own the moment a value is added or removed — there is
// no Save button — and every open form picks the change up at once.

export interface OptionListsSectionProps {
  notify: Notify;
  t: Tokens;
}

const ALL = 'All';

const sameList = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

interface OptionListCardProps {
  def: OptionListDefinition;
  values: string[];
  /** Whether the server holds a list for this key (as opposed to the defaults). */
  customised: boolean;
  notify: Notify;
  t: Tokens;
}

const OptionListCard = ({ def, values, customised, notify, t }: OptionListCardProps) => {
  const [draft, setDraft] = useState('');
  const module = MODULE_BY_ID[def.module] || (def.module === SETTINGS_TILE.id ? SETTINGS_TILE : undefined);
  const isDefault = sameList(values, def.defaults);

  const save = async (next: string[], done: string) => {
    const ok = await saveOptionList(def.key, next);
    notify(ok ? done : `Could not save "${def.label}" — the change is kept for now but not on the server.`, ok ? 'success' : 'error');
  };

  const add = () => {
    const value = draft.trim();
    if (!value) return;
    // Case-insensitively, the same rule every form applies when adding.
    if (values.some((v) => v.trim().toLowerCase() === value.toLowerCase())) {
      notify(`"${value}" is already in ${def.label}.`, 'error');
      return;
    }
    setDraft('');
    save([...values, value], `"${value}" added to ${def.label}.`);
  };

  const remove = (value: string) =>
    save(values.filter((v) => v !== value), `"${value}" removed from ${def.label}.`);

  const reset = () => save([...def.defaults], `${def.label} reset to the built-in values.`);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: '10px',
      padding: '14px 16px', borderRadius: t.radius.lg,
      border: `1px solid ${customised && !isDefault ? t.primaryBorder : t.border}`,
      backgroundColor: t.bgMain
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 'bold', color: t.textMain, fontSize: t.fontSize.md }}>{def.label}</div>
          <div style={{ color: t.textMuted, fontSize: t.fontSize.xs, marginTop: '2px' }}>
            {module ? `${module.icon} ${module.title}` : def.module} · {values.length} value{values.length === 1 ? '' : 's'}
            {!isDefault && ' · customised'}
          </div>
        </div>
        {!isDefault && (
          <Button type="button" variant="ghost" size="sm" onClick={reset} title="Back to the built-in values">
            ↺ Reset
          </Button>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {values.length === 0 && (
          <span style={{ color: t.textFaint, fontSize: t.fontSize.sm, fontStyle: 'italic' }}>
            Empty — users will type their own values.
          </span>
        )}
        {values.map((value) => (
          <span
            key={value}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '4px 6px 4px 10px', borderRadius: t.radius.pill,
              backgroundColor: t.primarySoft, color: t.textMain, fontSize: t.fontSize.sm,
              border: `1px solid ${t.primaryBorder}`
            }}
          >
            {value}
            <button
              type="button"
              onClick={() => remove(value)}
              aria-label={`Remove ${value}`}
              title="Remove"
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: t.textMuted, fontSize: t.fontSize.md, lineHeight: 1, padding: '0 2px'
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
        <Input
          placeholder="Add a value…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          containerStyle={{ flex: 1, marginBottom: 0 }}
        />
        <Button type="button" size="sm" onClick={add} disabled={!draft.trim()}>+ Add</Button>
      </div>
    </div>
  );
};

const OptionListsSection = ({ notify, t }: OptionListsSectionProps) => {
  const config = useChurchConfig();
  const serverLists = useMemo(() => parseOptionLists(config.optionLists), [config.optionLists]);

  const [search, setSearch] = useState('');
  const [activeModule, setActiveModule] = useState(ALL);

  // Only modules that own a list, in dashboard order, with Settings last.
  const moduleFilters = useMemo(() => {
    const owning = new Set(OPTION_LISTS.map((d) => d.module));
    return [
      ...MODULES.filter((m) => owning.has(m.id)),
      ...(owning.has(SETTINGS_TILE.id) ? [SETTINGS_TILE] : [])
    ];
  }, []);

  const needle = search.trim().toLowerCase();
  const shown = OPTION_LISTS.filter((def) => {
    if (activeModule !== ALL && def.module !== activeModule) return false;
    if (!needle) return true;
    const values = serverLists[def.key] || def.defaults;
    return def.label.toLowerCase().includes(needle)
      || values.some((v) => v.toLowerCase().includes(needle));
  });

  const pill = (selected: boolean) => ({
    padding: '7px 14px', minHeight: '36px', borderRadius: t.radius.pill,
    fontWeight: 'bold' as const, fontSize: t.fontSize.sm, cursor: 'pointer', whiteSpace: 'nowrap' as const,
    border: `1px solid ${selected ? t.primary : t.border}`,
    backgroundColor: selected ? t.primarySoft : t.bgMain,
    color: selected ? t.textAccent : t.textMuted
  });

  return (
    <div>
      <p style={{ color: t.textMuted, fontSize: t.fontSize.md, margin: '0 0 14px' }}>
        These are the choices offered in each module's dropdowns. Changes save
        immediately and reach every user. Removing a value does not alter records
        that already use it — they keep the text they were saved with.
      </p>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '12px' }}>
        <Input
          label="Find a list or a value"
          placeholder="e.g. designation, Sunday, Treasurer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          containerStyle={{ flex: '1 1 260px', marginBottom: 0 }}
        />
      </div>

      <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '6px', marginBottom: t.spacing.lg }}>
        <button type="button" style={pill(activeModule === ALL)} onClick={() => setActiveModule(ALL)}>
          All ({OPTION_LISTS.length})
        </button>
        {moduleFilters.map((m) => (
          <button
            key={m.id}
            type="button"
            style={pill(activeModule === m.id)}
            onClick={() => setActiveModule(m.id)}
            aria-pressed={activeModule === m.id}
          >
            {m.icon} {m.title}
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '12px' }}>
        {shown.map((def) => (
          <OptionListCard
            key={def.key}
            def={def}
            values={serverLists[def.key] ?? [...def.defaults]}
            customised={def.key in serverLists}
            notify={notify}
            t={t}
          />
        ))}
      </div>

      {shown.length === 0 && (
        <div style={{
          padding: t.spacing.xxl, textAlign: 'center', color: t.textFaint,
          backgroundColor: t.bgMain, borderRadius: t.radius.xl, border: `1px dashed ${t.border}`
        }}>
          No list matches &quot;<strong>{search}</strong>&quot;.
        </div>
      )}
    </div>
  );
};

export default OptionListsSection;
